import React from "react";
import { graphql, useFragment } from "react-relay";

import { faCalendar, faLink, faMicrophone } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { startOfMinute } from "date-fns";

import { zindexRadixPopper } from "~/css/zindexes";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Tooltip, useModals } from "~/ui";
import Menu from "~/ui/menu";
import type { MultiSelectorOption } from "~/ui/selectors/MultiSelector";

import {
	DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG,
	useCopilotPaywallsEnabled,
	WEB_BOTLESS_RECORDING_FEATURE_FLAG,
} from "../meetingRecorder/featureFlag";
import EventFormModal from "../nav/panels/Meetings/EventFormModal/EventFormModal";
import OutOfCreditsModal from "../stream/NoteHeader/OutOfCreditsModal";
import RecordLiveMeetingModal from "./RecordLiveMeetingModal";
import UploadMenuItem from "./uploads/UploadMenuItem";

import type { AddToLibraryMenu_viewer$key } from "~/__generated__/AddToLibraryMenu_viewer.graphql";

const viewerFragment = graphql`
	fragment AddToLibraryMenu_viewer on Viewer {
		remainingMeetingRecordingCredits
		hasAccessToCopilot
		account {
			allowBotlessRecording
			billingAccount {
				subscriptionBillingCycleAnchor
			}
			uploadCredits {
				includedUploadCredits
				includedCreditsUsedCurrentCycle
			}
		}
		...featureFlag_useCopilotPaywallsOn_viewer
		...EventFormModal_viewer
		...BillingIdentityFeaturesPaywall_viewer
		...UploadMenuItem_viewer
	}
`;

export default function AddToLibraryMenu({
	viewer: viewerKey,
	defaultChannels,
	analyticsLocationDescription,
	align,
	children,
}: {
	viewer: AddToLibraryMenu_viewer$key;
	defaultChannels: ReadonlyArray<MultiSelectorOption> | null;
	analyticsLocationDescription: string;
	align?: "end" | "start" | "center";
	children: React.ReactNode;
}) {
	const modals = useModals();

	const viewer = useFragment(viewerFragment, viewerKey);
	const { remainingMeetingRecordingCredits, hasAccessToCopilot } = viewer;

	// Check if botless recording should be available
	const isWebBotlessRecordingEnabled = useFeatureIsOn(WEB_BOTLESS_RECORDING_FEATURE_FLAG);
	const isDesktopBotlessRecordingEnabled = useFeatureIsOn(DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG);
	const allowBotlessRecording = viewer.account.allowBotlessRecording;
	// We have the web and the desktop botless recording
	// If the user is in the web we can just check for the botless recording flag
	// If the user is in the desktop we have an additional check for the desktop recorder feature flag
	// And if the user has the desktop recorder available
	const shouldShowBotlessRecording =
		(isWebBotlessRecordingEnabled || (isDesktopBotlessRecordingEnabled && window.desktopRecorder)) &&
		allowBotlessRecording;

	const paywallsEnabled = useCopilotPaywallsEnabled(viewer);
	const outOfCredits = remainingMeetingRecordingCredits === 0 && paywallsEnabled;

	const openRecordLiveMeetingModal = React.useCallback(() => {
		dispatchAnalyticsTrack("Clicked start recording with URL", { location: analyticsLocationDescription });
		modals.open(({ close }) => <RecordLiveMeetingModal onClose={close} />);
	}, [analyticsLocationDescription, modals]);

	const openRecordingsPaywall = React.useCallback(
		() => modals.open(({ close }) => <OutOfCreditsModal onClose={close} />),
		[modals],
	);

	const onCreateEvent = React.useCallback(() => {
		const now = new Date();
		modals?.open(({ close }) => (
			<EventFormModal
				viewer={viewer}
				onFormFinish={close}
				initialData={{
					start: startOfMinute(now),
				}}
			/>
		));
	}, [modals, viewer]);

	const RecordingMenuItem = ({
		icon,
		onSelect,
		children,
	}: {
		icon: IconDefinition;
		iconProps?: Omit<FontAwesomeIconProps, "icon">;
		onSelect: () => void;
		children: string;
	}) => (
		<Tooltip
			disabled={hasAccessToCopilot}
			content="Fellow Note Taker is not enabled"
			placement="bottom"
			zIndex={zindexRadixPopper}
		>
			<Menu.Item icon={icon} onSelect={outOfCredits ? openRecordingsPaywall : onSelect}>
				{children}
			</Menu.Item>
		</Tooltip>
	);

	const recordNow = React.useCallback(() => {
		if (window.desktopRecorder && isDesktopBotlessRecordingEnabled) {
			window.desktopRecorder.initiateRecording({});
			return;
		}
		// TODO: Mic only web recording will go here
	}, [isDesktopBotlessRecordingEnabled]);

	return (
		<Menu>
			<Menu.Trigger>{children}</Menu.Trigger>
			<Menu.Content align={align ?? "end"}>
				{shouldShowBotlessRecording && (
					<RecordingMenuItem icon={faMicrophone} onSelect={recordNow}>
						Record now botless
					</RecordingMenuItem>
				)}
				<RecordingMenuItem icon={faLink} onSelect={openRecordLiveMeetingModal}>
					Record via link…
				</RecordingMenuItem>
				<RecordingMenuItem icon={faCalendar} onSelect={onCreateEvent}>
					Schedule new meeting
				</RecordingMenuItem>
				<UploadMenuItem
					viewer={viewer}
					analyticsLocationDescription="Add to library menu"
					defaultChannels={defaultChannels}
				/>
			</Menu.Content>
		</Menu>
	);
}
